// EN-1734: pure helpers for the Device Info "Data" block (Super SIM usage
// against the Fleet data limit). No store, no axios, so unit specs import them
// directly.
import { ISimDataUsage } from '../models/SimDataUsage';

/** Decimal megabytes, as the WebApi converts the KORE Fleet data_limit. */
export const BYTES_PER_MB = 1_000_000;

/** Fetch usage for one AGM at most this often (the WebApi caches 10 min). */
export const SIM_DATA_USAGE_MIN_REFETCH_MS = 3 * 60 * 1000;

/** What the block shows. */
export type SimDataUsageKind =
  | 'none' // nothing: no answer yet
  | 'nosim' // the WebApi found no SIM for this AGM (neutral line)
  | 'unavailable' // a SIM, but no usable figures, or the call failed (neutral line)
  | 'ok'
  | 'warning'
  | 'limit';

/** The neutral line when the WebApi answers hasSim false. */
export const SIM_DATA_NO_SIM_TEXT =
  'Data usage cannot be determined (no SIM found for this AGM)';

/** The neutral line when there is a SIM but no usable figures. */
export const SIM_DATA_UNAVAILABLE_TEXT = 'Data usage unavailable';

/** Locale / time zone overrides, for deterministic specs. */
export interface SimDataUsageFormatOptions {
  locale?: string;
  timeZone?: string;
}

/**
 * Parses a WebApi date: 'yyyy-MM-dd HH:mm:ss[.ffffff]' (UTC, no zone — the
 * API-wide Newtonsoft format) or ISO 8601 with or without Z / an offset.
 * Date-times without a zone are read as UTC; a date-only 'yyyy-MM-dd' is left
 * as is (already UTC midnight in every engine; 'yyyy-MM-ddZ' is invalid in
 * Safari). Invalid input gives null.
 */
export function parseApiUtcDate(value?: string | null): Date | null {
  if (!value || typeof value !== 'string') return null;
  let text = value.trim().replace(' ', 'T');
  // JavaScript Date keeps at most milliseconds.
  text = text.replace(/(\.\d{3})\d+/, '$1');
  const hasTime = /T\d{2}:\d{2}/.test(text);
  if (hasTime && !/(Z|[+-]\d{2}:?\d{2})$/i.test(text)) text += 'Z';
  const date = new Date(text);
  return isNaN(date.getTime()) ? null : date;
}

/** How the block renders this answer. */
export function simDataUsageKind(
  usage?: ISimDataUsage | null
): SimDataUsageKind {
  if (!usage) return 'none';
  if (!usage.hasSim) return 'nosim';
  const hasFigures =
    typeof usage.usedBytes === 'number' &&
    typeof usage.limitBytes === 'number' &&
    usage.limitBytes > 0;
  if (!hasFigures) return 'unavailable';
  switch (usage.state) {
    case 'ok':
    case 'warning':
    case 'limit':
      return usage.state;
    default:
      return 'unavailable';
  }
}

/**
 * What the Device Info panel shows for an AGM (EN-1734). Independent of the
 * AGM's current comm mode: an AGM that hit its cellular limit may be on
 * Iridium, which is when the banner matters most.
 * - usage loaded: as simDataUsageKind (hasSim false gives the neutral
 *   "no SIM found" line);
 * - no usage and the last call failed: the neutral "unavailable" line;
 * - otherwise (not loaded yet): nothing.
 */
export function simDataUsageDisplayKind(
  usage: ISimDataUsage | null | undefined,
  lastCallFailed: boolean
): SimDataUsageKind {
  if (usage) return simDataUsageKind(usage);
  return lastCallFailed ? 'unavailable' : 'none';
}

/** Whole megabytes, rounded down (so 499.9 MB used never reads 500). */
export function formatMegabytes(bytes?: number | null): string {
  if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) return '0';
  return Math.floor(bytes / BYTES_PER_MB).toLocaleString('en-US');
}

/** Whole percent, rounded down like the server's thresholds (89.9 → 89). */
export function formatPercent(percent?: number | null): string {
  if (typeof percent !== 'number' || !isFinite(percent) || percent < 0)
    return '0%';
  return `${Math.floor(percent)}%`;
}

/** "Data: 423 / 500 MB (84%)". */
export function simDataUsageLabel(usage: ISimDataUsage): string {
  return `Data: ${formatMegabytes(usage.usedBytes)} / ${formatMegabytes(
    usage.limitBytes
  )} MB (${formatPercent(usage.percent)})`;
}

/** q-linear-progress value, 0..1. */
export function simDataUsageProgress(usage: ISimDataUsage): number {
  const percent = usage.percent;
  if (typeof percent !== 'number' || !isFinite(percent) || percent <= 0)
    return 0;
  return Math.min(1, percent / 100);
}

/**
 * Quasar colour of the warning text and icon: deep-orange-10 (#bf360c), about
 * 5.6:1 on white, so it passes WCAG AA for normal text (amber-9 was ~2.2:1).
 */
export const SIM_DATA_WARNING_TEXT_COLOR = 'deep-orange-10';

/** Its hex value, for the contrast check in the unit spec. */
export const SIM_DATA_WARNING_TEXT_HEX = '#bf360c';

/** "Approaching data limit (92% used)": the ≥90 % warning in words. */
export function simDataWarningText(usage: ISimDataUsage): string {
  return `Approaching data limit (${formatPercent(usage.percent)} used)`;
}

/** Quasar colour of the bar. */
export function simDataUsageColor(kind: SimDataUsageKind): string {
  if (kind === 'limit') return 'negative';
  if (kind === 'warning') return 'amber-8';
  return 'primary';
}

/**
 * The billing period end in the viewer's time zone, with the time and zone,
 * e.g. "Nov 13, 2026, 20:00 AST" for 2026-11-14 00:00 UTC in Halifax. KORE
 * periods end at 00:00 UTC, which is the evening before west of UTC, so a
 * date alone would read a day late or early; '' if unknown.
 */
export function formatResetDate(
  periodEnd?: string | null,
  options: SimDataUsageFormatOptions = {}
): string {
  const date = parseApiUtcDate(periodEnd);
  if (!date) return '';
  return date.toLocaleString(options.locale || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
    timeZone: options.timeZone,
  });
}

/** Local hh:mm (24 h) of when the figures were read; '' if unknown. */
export function formatUpdatedTime(
  asOfUtc?: string | null,
  options: SimDataUsageFormatOptions = {}
): string {
  const date = parseApiUtcDate(asOfUtc);
  if (!date) return '';
  return date.toLocaleTimeString(options.locale || 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: options.timeZone,
  });
}

/** "Resets Nov 13, 2026, 20:00 AST"; '' if unknown. */
export function simDataResetLabel(
  usage: ISimDataUsage,
  options: SimDataUsageFormatOptions = {}
): string {
  const date = formatResetDate(usage.periodEnd, options);
  return date ? `Resets ${date}` : '';
}

/** "Updated 14:05"; '' if unknown. */
export function simDataUpdatedLabel(
  usage: ISimDataUsage,
  options: SimDataUsageFormatOptions = {}
): string {
  const time = formatUpdatedTime(usage.asOfUtc, options);
  return time ? `Updated ${time}` : '';
}

/** The red banner text at the limit. */
export function simDataLimitBannerText(
  usage: ISimDataUsage,
  options: SimDataUsageFormatOptions = {}
): string {
  const date = formatResetDate(usage.periodEnd, options);
  return date
    ? `Data limit reached. This AGM can't connect over cellular until ${date}.`
    : "Data limit reached. This AGM can't connect over cellular until its data plan resets.";
}

/**
 * Whether the periodic refresh may call the WebApi: not while the tab is
 * hidden (it refreshes when the tab is shown again).
 */
export function simDataUsagePollAllowed(
  doc: { hidden?: boolean } | undefined = typeof document === 'undefined'
    ? undefined
    : document
): boolean {
  return !doc?.hidden;
}

/** True when usage for this AGM was fetched too recently to ask again. */
export function isSimDataUsageFresh(
  lastFetchedAtMs: number | undefined,
  nowMs: number,
  minIntervalMs = SIM_DATA_USAGE_MIN_REFETCH_MS
): boolean {
  return (
    typeof lastFetchedAtMs === 'number' &&
    nowMs - lastFetchedAtMs >= 0 &&
    nowMs - lastFetchedAtMs < minIntervalMs
  );
}
